import { ProblemError } from "@cosmosai/api-client";
import { CircleXIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import { CopyButton } from "./copy-button";

interface Described {
  title: string;
  detail: string | undefined;
  code: string | undefined;
  traceId: string | undefined;
}

/** What failed, from a problem document (RFC 9457) or any other error. */
export function describeError(error: unknown, fallbackTitle: string): Described {
  if (error instanceof ProblemError) {
    return {
      title: error.problem.title || fallbackTitle,
      detail: error.problem.detail,
      code: error.code,
      traceId: error.requestId,
    };
  }
  const detail = error instanceof Error ? error.message : String(error);
  return { title: fallbackTitle, detail, code: undefined, traceId: undefined };
}

/** An error message that says what failed and shows the trace id for support. */
export function ErrorState({
  error,
  title,
  action,
  className,
}: {
  error: unknown;
  title?: string;
  action?: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  const described = describeError(error, title ?? t("ui.error.title"));
  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2.5 rounded-sm bg-crit-soft px-3.5 py-3 text-base text-ink",
        className,
      )}
    >
      <CircleXIcon aria-hidden className="mt-0.5 size-[18px] shrink-0 text-crit" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{described.title}</p>
        {described.detail && <p className="mt-0.5 text-ink-2">{described.detail}</p>}
        {described.code && <p className="mt-0.5 font-mono text-sm text-ink-3">{described.code}</p>}
        {described.traceId && (
          <p className="mt-1 flex items-center gap-1 text-sm text-ink-3">
            {t("ui.error.trace")}: <code className="text-ink-2">{described.traceId}</code>
            <CopyButton value={described.traceId} label={t("ui.error.copyTrace")} />
          </p>
        )}
        {action && <div className="mt-2.5">{action}</div>}
      </div>
    </div>
  );
}
